import { atom } from "jotai"
import { store } from "./store"

export const DEFAULT_MAINTENANCE_MESSAGE =
	"Photo Editor is temporarily unavailable for scheduled maintenance. Please check back soon."
export const MAINTENANCE_MODE_REASON = "maintenance_mode"

const MAINTENANCE_STATUS_FETCH_TIMEOUT_MS = 2500

export interface MaintenanceModeState {
	enabled: boolean
	message: string
}

export type MaintenanceModeStatusFetchResult =
	| { ok: true; state: MaintenanceModeState }
	| { ok: false }

export const disabledMaintenanceModeState: MaintenanceModeState = {
	enabled: false,
	message: DEFAULT_MAINTENANCE_MESSAGE,
}

export const maintenanceModeAtom = atom<MaintenanceModeState>(disabledMaintenanceModeState)

const isRecord = (value: unknown): value is Record<string, unknown> => {
	return typeof value === "object" && value !== null && !Array.isArray(value)
}

const optionalStringOrNull = (value: unknown): string | null => {
	if (typeof value !== "string") {
		return null
	}

	const trimmed = value.trim()
	return trimmed ? trimmed : null
}

export const parseMaintenanceModeStatusResponse = (value: unknown): MaintenanceModeState | null => {
	if (!isRecord(value)) {
		return null
	}

	if (typeof value.isEnabled !== "boolean") {
		return null
	}

	if (!value.isEnabled) {
		return disabledMaintenanceModeState
	}

	return {
		enabled: true,
		message: optionalStringOrNull(value.message) ?? DEFAULT_MAINTENANCE_MESSAGE,
	}
}

export const fetchMaintenanceModeStatusState = async (
	maintenanceStatusUrl: string | undefined,
): Promise<MaintenanceModeStatusFetchResult> => {
	const url = optionalStringOrNull(maintenanceStatusUrl)
	if (!url) {
		return { ok: false }
	}

	const controller = new AbortController()
	const timeoutId = globalThis.setTimeout(
		() => controller.abort(),
		MAINTENANCE_STATUS_FETCH_TIMEOUT_MS,
	)

	try {
		const response = await fetch(url, {
			cache: "no-store",
			headers: { Accept: "application/json" },
			signal: controller.signal,
		})
		if (!response.ok) {
			return { ok: false }
		}

		const state = parseMaintenanceModeStatusResponse(await response.json())
		if (!state) {
			return { ok: false }
		}

		return { ok: true, state }
	} catch {
		return { ok: false }
	} finally {
		globalThis.clearTimeout(timeoutId)
	}
}

export const parseMaintenanceModeAPIResponse = (value: unknown): MaintenanceModeState | null => {
	if (!isRecord(value) || !isRecord(value.detail)) {
		return null
	}

	if (value.detail.reason !== MAINTENANCE_MODE_REASON) {
		return null
	}

	return {
		enabled: true,
		message: optionalStringOrNull(value.detail.message) ?? DEFAULT_MAINTENANCE_MESSAGE,
	}
}

export const setMaintenanceModeFromAPIResponse = (value: unknown): void => {
	const state = parseMaintenanceModeAPIResponse(value)
	if (!state) {
		return
	}

	store.set(maintenanceModeAtom, state)
}
